import { useEffect, useRef } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useDashboardStore } from './dashboardStore'

export interface SavedFilterState {
  statusFilter: string[]
  employeeFilter: number[]
  templateFilter: number[]
  orderDateFrom: string | null
  orderDateTo: string | null
  deliveryDateFrom: string | null
  deliveryDateTo: string | null
  orderIdFrom: number | null
  orderIdTo: number | null
  customerNameFilter: string
  referenceFilter: string
  yourRefFilter: string
  infoFilter: string
  phoneFilter: string
  emailFilter: string
  customerIdFilter: string
  deliveryNameFilter: string
  searchQuery: string
}

export interface SavedView {
  id: string
  name: string
  filters: SavedFilterState
}

interface ViewsState {
  views: SavedView[]
  activeViewId: string | null // null = "Alle ordrer"
  setActiveView: (id: string | null) => void
  createView: (name: string) => string
  deleteView: (id: string) => void
  renameView: (id: string, name: string) => void
  saveFiltersToView: (id: string) => void
}

const FILTER_KEYS: (keyof SavedFilterState)[] = [
  'statusFilter',
  'employeeFilter',
  'templateFilter',
  'orderDateFrom',
  'orderDateTo',
  'deliveryDateFrom',
  'deliveryDateTo',
  'orderIdFrom',
  'orderIdTo',
  'customerNameFilter',
  'referenceFilter',
  'yourRefFilter',
  'infoFilter',
  'phoneFilter',
  'emailFilter',
  'customerIdFilter',
  'deliveryNameFilter',
  'searchQuery',
]

/** Extract current filter state from dashboardStore */
export function extractFilters(): SavedFilterState {
  const s = useDashboardStore.getState()
  const filters = {} as SavedFilterState
  for (const key of FILTER_KEYS) {
    ;(filters as unknown as Record<string, unknown>)[key] = s[key]
  }
  return filters
}

/** Apply saved filters to dashboardStore (single setState to avoid re-renders) */
export function applyFilters(filters: SavedFilterState) {
  useDashboardStore.setState({ ...filters, pageIndex: 0 })
}

/** Reset dashboardStore to default filter state */
export function applyDefaultFilters() {
  useDashboardStore.setState({
    statusFilter: [],
    employeeFilter: [],
    templateFilter: [],
    orderDateFrom: null,
    orderDateTo: null,
    deliveryDateFrom: null,
    deliveryDateTo: null,
    orderIdFrom: null,
    orderIdTo: null,
    customerNameFilter: '',
    referenceFilter: '',
    yourRefFilter: '',
    infoFilter: '',
    phoneFilter: '',
    emailFilter: '',
    customerIdFilter: '',
    deliveryNameFilter: '',
    searchQuery: '',
    pageIndex: 0,
  })
}

export const useViewsStore = create<ViewsState>()(
  persist(
    (set, get) => ({
      views: [],
      activeViewId: null,

      setActiveView: (id) => set({ activeViewId: id }),

      createView: (name) => {
        const id = crypto.randomUUID()
        const filters = extractFilters()
        set((state) => ({
          views: [...state.views, { id, name, filters }],
          activeViewId: id,
        }))
        return id
      },

      deleteView: (id) => {
        const { activeViewId } = get()
        set((state) => ({
          views: state.views.filter((v) => v.id !== id),
          activeViewId: activeViewId === id ? null : activeViewId,
        }))
        if (activeViewId === id) {
          applyDefaultFilters()
        }
      },

      renameView: (id, name) => {
        set((state) => ({
          views: state.views.map((v) =>
            v.id === id ? { ...v, name } : v
          ),
        }))
      },

      saveFiltersToView: (id) => {
        const filters = extractFilters()
        set((state) => ({
          views: state.views.map((v) =>
            v.id === id ? { ...v, filters } : v
          ),
        }))
      },
    }),
    { name: 'admin-saved-views' }
  )
)

/**
 * Auto-save hook: debounces dashboardStore filter changes
 * and persists them to the active saved view.
 */
export function useAutoSaveView() {
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    const unsub = useDashboardStore.subscribe(() => {
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        const { activeViewId } = useViewsStore.getState()
        if (activeViewId) {
          useViewsStore.getState().saveFiltersToView(activeViewId)
        }
      }, 500)
    })

    return () => {
      unsub()
      clearTimeout(timerRef.current)
    }
  }, [])
}
